import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { useVault } from "@/context/VaultContext";
import { searchNotes } from "@/lib/search";
import { STATUSES } from "@/lib/store";

const EMPTY = { folderId: "", status: "", range: "", from: "", to: "" };
const RANGES = [["7d", "7 days"], ["30d", "30 days"], ["12m", "12 months"]];

function Chip({ on, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`shrink-0 rounded-full border px-3 py-1 text-xs transition-colors ${on ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground"}`}>
      {children}
    </button>
  );
}

function Mark({ text, i, len }) {
  if (i < 0) return text;
  return <>{text.slice(0, i)}<mark className="bg-foreground text-background px-0.5">{text.slice(i, i + len)}</mark>{text.slice(i + len)}</>;
}

function Filters({ f, set, folders }) {
  const tog = (k, v) => set({ ...f, [k]: f[k] === v ? "" : v });
  return (
    <div className="space-y-2 border-b px-3 py-3 min-w-0" data-testid="search-filters">
      <div className="flex gap-1.5 overflow-x-auto pb-1 pr-6 [mask-image:linear-gradient(to_right,#000_85%,transparent)] [-webkit-mask-image:linear-gradient(to_right,#000_85%,transparent)]">{folders.map((x) => <Chip key={x.id} on={f.folderId === x.id} onClick={() => tog("folderId", x.id)} testid={`search-folder-chip-${x.id}`}>{x.name}</Chip>)}</div>
      <div className="flex gap-1.5 overflow-x-auto pb-1 pr-6 [mask-image:linear-gradient(to_right,#000_85%,transparent)] [-webkit-mask-image:linear-gradient(to_right,#000_85%,transparent)]">{STATUSES.map((s) => <Chip key={s} on={f.status === s} onClick={() => tog("status", s)} testid={`search-status-chip-${s.toLowerCase().replace(/\s/g, "-")}`}>{s}</Chip>)}</div>
      <div className="flex flex-wrap items-center gap-1.5">
        {RANGES.map(([k, l]) => <Chip key={k} on={f.range === k} onClick={() => set({ ...f, range: f.range === k ? "" : k, from: "", to: "" })} testid={`search-range-chip-${k}`}>{l}</Chip>)}
        {Object.values(f).some(Boolean) && <button type="button" onClick={() => set(EMPTY)} className="ml-auto text-xs underline" data-testid="search-clear-filters">Clear filters</button>}
      </div>
      <div className="flex items-center gap-1.5">
        <input type="date" value={f.from} onChange={(e) => set({ ...f, range: "", from: e.target.value })} aria-label="From date" data-testid="search-date-from"
          className="min-w-0 flex-1 h-7 rounded-full border border-border bg-background px-3 text-xs transition-colors hover:border-foreground focus:border-foreground focus:outline-none" />
        <span className="shrink-0 text-xs text-muted-foreground">to</span>
        <input type="date" value={f.to} onChange={(e) => set({ ...f, range: "", to: e.target.value })} aria-label="To date" data-testid="search-date-to"
          className="min-w-0 flex-1 h-7 rounded-full border border-border bg-background px-3 text-xs transition-colors hover:border-foreground focus:border-foreground focus:outline-none" />
      </div>
    </div>
  );
}

function Result({ r, active, folder, onOpen, onHover }) {
  return (
    <button type="button" onClick={onOpen} onMouseEnter={onHover} data-testid={`search-result-${r.note.id}`}
      className={`block w-full min-w-0 text-left px-4 py-3 border-b transition-colors ${active ? "bg-foreground text-background" : ""}`}>
      <div className="flex items-baseline gap-2">
        <span className="font-heading font-semibold truncate">{r.note.title || "Untitled"}</span>
        <span className={`ml-auto shrink-0 font-mono text-[11px] ${active ? "" : "text-muted-foreground"}`}>{r.date}</span>
      </div>
      <p className={`text-xs ${active ? "" : "text-muted-foreground"}`}>{folder}{r.note.ref ? ` · Ref ${r.note.ref}` : ""}{r.note.receipt?.status ? ` · ${r.note.receipt.status}` : ""}</p>
      {r.hit && <p className="mt-1 text-sm truncate" data-testid="search-result-snippet"><span className="font-mono text-[10px] uppercase tracking-widest mr-2">{r.hit.label}</span><Mark {...r.hit} /></p>}
    </button>
  );
}

// Panel height tracks the visual viewport so the on-screen keyboard never covers results
function usePanelHeight(open, wrap) {
  const [h, setH] = useState(420);
  useEffect(() => {
    if (!open) return;
    const vv = window.visualViewport;
    const calc = () => {
      const bottom = wrap.current?.getBoundingClientRect().bottom ?? 0;
      setH(Math.max(180, Math.min(560, (vv ? vv.height + vv.offsetTop : window.innerHeight) - bottom - 16)));
    };
    calc();
    vv?.addEventListener("resize", calc); vv?.addEventListener("scroll", calc); window.addEventListener("resize", calc);
    return () => { vv?.removeEventListener("resize", calc); vv?.removeEventListener("scroll", calc); window.removeEventListener("resize", calc); };
  }, [open, wrap]);
  return h;
}

export const GlobalSearch = forwardRef(function GlobalSearch(_, ref) {
  const { notes, folders, folderName, setFolderId, setView, setNoteId } = useVault();
  const wrap = useRef(null);
  const input = useRef(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [f, setF] = useState(EMPTY);
  const [active, setActive] = useState(0);
  const maxH = usePanelHeight(open, wrap);
  const hasQuery = q.trim() || Object.values(f).some(Boolean);
  const results = useMemo(() => (hasQuery ? searchNotes(notes, q, f) : []), [notes, q, f, hasQuery]);
  useEffect(() => setActive(0), [q, f]);
  useImperativeHandle(ref, () => ({ focus: () => { input.current?.focus(); setOpen(true); } }));
  useEffect(() => {
    if (!open) return;
    const away = (e) => { if (!wrap.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", away);
    return () => document.removeEventListener("pointerdown", away);
  }, [open]);
  const close = () => { setOpen(false); input.current?.blur(); };
  const go = (n) => { close(); setFolderId(n.folderId); setView("folder"); setNoteId(n.id); };
  const onKey = (e) => {
    if (e.key === "Escape") { e.preventDefault(); close(); }
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    if (e.key === "Enter" && results[active]) go(results[active].note);
  };
  return (
    <div ref={wrap} className="relative order-last basis-full min-w-0 md:order-none md:basis-auto md:flex-1 md:max-w-xl md:ml-4" data-testid="global-search">
      <div className={`flex h-10 w-full items-center gap-2 rounded-md border bg-background px-3 transition-colors ${open ? "border-foreground" : ""}`}>
        <Search className="h-4 w-4 shrink-0" />
        <input ref={input} type="text" inputMode="search" enterKeyHint="search" autoComplete="off" value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)} onKeyDown={onKey} placeholder="Search notes, Ref #, tracking, date…" aria-label="Search all notes"
          className="min-w-0 flex-1 bg-transparent text-base md:text-sm outline-none [&::-webkit-search-cancel-button]:hidden" data-testid="global-search-input" />
        {q ? <button type="button" onClick={() => { setQ(""); input.current?.focus(); }} aria-label="Clear search" data-testid="global-search-clear"><X className="h-4 w-4" /></button>
          : <kbd className="hidden md:inline font-mono text-[10px] border rounded px-1.5 text-muted-foreground">Ctrl K</kbd>}
      </div>
      {open && (
        <div style={{ maxHeight: maxH }} className="absolute inset-x-0 top-full z-40 mt-2 flex flex-col overflow-hidden rounded-lg border bg-background shadow-2xl sv-rise" data-testid="global-search-panel">
          <Filters f={f} set={setF} folders={folders} />
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain" data-testid="global-search-results">
            {results.map((r, i) => <Result key={r.note.id} r={r} active={i === active} folder={folderName(r.note.folderId)} onOpen={() => go(r.note)} onHover={() => setActive(i)} />)}
            {hasQuery && !results.length && <p className="p-5 text-sm text-muted-foreground" data-testid="global-search-empty">No notes match.</p>}
            {!hasQuery && <p className="p-5 text-sm text-muted-foreground" data-testid="global-search-hint">Type to search every folder, or pick a filter. ↑↓ to move · Enter to open · Esc to close</p>}
          </div>
          {hasQuery && <p className="shrink-0 border-t px-4 py-2 font-mono text-[11px] text-muted-foreground" data-testid="global-search-count">{results.length}{results.length === 50 ? "+" : ""} result{results.length === 1 ? "" : "s"}</p>}
        </div>
      )}
    </div>
  );
});
